import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'
import type { PaginatedResponse } from './pagination'

// ---------------------------------------------------------------------------
// Types — mirror the `mobile-app-api/v1/me/workouts` DTOs
// ---------------------------------------------------------------------------

export interface TrainingCenterSummary {
  id: string
  name: string
}

export interface WorkoutLocation {
  id: string
  name: string
  address: string
  latitude: number
  longitude: number
}

export interface WorkoutTrainingClass {
  id: string
  level?: string | null
}

/** Raw `TrainingCenterClassOccurrenceStatus` values. */
export type WorkoutOccurrenceStatus = 'scheduled' | 'canceled'
/** Raw `TrainingCenterClassOccurrenceCustomerStatus` values. */
export type WorkoutParticipantStatus = 'confirmed' | 'cancelled'
/** Raw `TrainingCenterClassOccurrenceCustomerType` values. */
export type WorkoutParticipantType = 'contract_class' | 'extra_class'

export interface WorkoutCustomerParticipant {
  id: string
  status: WorkoutParticipantStatus
  type: WorkoutParticipantType
  confirmedAt?: string | null
  cancelledAt?: string | null
}

/** Whose item this is when the account acts for more than one student (guardian scope). */
export interface WorkoutStudentCustomer {
  id: string
  name: string
}

/**
 * A participation of the occurrence the account can act on (self or a
 * dependent). More than one entry means the UI offers a person picker for the
 * presence actions.
 */
export interface WorkoutScopeParticipant {
  participantId: string
  studentCustomer: WorkoutStudentCustomer
  isDependent: boolean
  status: WorkoutParticipantStatus
  type: WorkoutParticipantType
  confirmedAt?: string | null
  cancelledAt?: string | null
}

export interface WorkoutListItem {
  id: string
  trainingCenterId: string
  trainingCenterClassId?: string | null
  trainingCenter: TrainingCenterSummary
  trainingClass?: WorkoutTrainingClass | null
  date: string
  startTimeMin: number
  endTimeMin: number
  level?: string | null
  status: WorkoutOccurrenceStatus
  selfEnrollmentMode: string
  location: WorkoutLocation
  maxParticipants?: number | null
  confirmedParticipantsCount: number
  customerParticipant?: WorkoutCustomerParticipant | null
  /** Present on `me/workouts` when the account has dependents. */
  studentCustomer?: WorkoutStudentCustomer | null
  isDependent?: boolean | null
  createdAt?: string | null
  updatedAt?: string | null
  /** Presente na agenda de auto-agendamento; ausente em 'minhas aulas'. */
  selfEnrollment?: { available: boolean; blockReason?: string | null }
}

export interface WorkoutParticipant {
  id: string
  customer: { id: string; name: string; level?: string | null }
  type: WorkoutParticipantType
  confirmedAt?: string | null
  cancelledAt?: string | null
}

export interface WorkoutTeacher {
  id: string
  memberId: string
  name: string
  status: string
}

export interface WorkoutDetail {
  id: string
  participantId: string
  trainingCenter: TrainingCenterSummary
  location: WorkoutLocation
  trainingClass?: WorkoutTrainingClass | null
  date: string
  startTimeMin: number
  endTimeMin: number
  level?: string | null
  occurrenceStatus: WorkoutOccurrenceStatus
  myParticipantStatus: WorkoutParticipantStatus
  myParticipantType: WorkoutParticipantType
  confirmedAt?: string | null
  cancelledAt?: string | null
  cancellation?: { mode: 'with_make_up' | 'without_make_up' } | null
  confirmation?: Record<string, never> | null
  participants: { confirmed: WorkoutParticipant[]; cancelled: WorkoutParticipant[] }
  teachers: WorkoutTeacher[]
  /**
   * Every participation of the occurrence the account acts for — the entry
   * matching `participantId` is this detail's focus. More than one entry means
   * the UI offers a person picker.
   */
  scopeParticipants?: WorkoutScopeParticipant[] | null
  createdAt?: string | null
  updatedAt?: string | null
}

export type WorkoutCancelKind =
  | 'blocked'
  | 'contractWithMakeup'
  | 'contractWithoutMakeup'
  | 'makeupCreditRelease'
  | 'makeupCreditKept'
  | 'extraChargeCancellation'
  | 'extraChargeOpenKept'
  | 'extraChargeKeptWithCompensation'
  | 'extraChargePaidKept'

export interface WorkoutCancelConsequence {
  type: string
  makeup?: {
    creditId?: string | null
    expiresAt: string
    becomesUsableAt?: string | null
    resultingStatus: string
  } | null
  charge?: {
    chargeId: string
    totalAmountCents: number
    status: string
    dueAt?: string | null
  } | null
  chargeKeptReason?: string | null
}

export interface WorkoutCancelPreview {
  kind: WorkoutCancelKind
  blockReason?: string | null
  consequences: WorkoutCancelConsequence[]
  participantSummary: {
    participantId: string
    trainingCenterCustomerId: string
    customerName?: string | null
    occurrenceId: string
    occurrenceDate: string
    occurrenceStartTimeMin: number
    occurrenceEndTimeMin: number
    locationName?: string | null
  }
}

class WorkoutService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '',
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  async listWorkouts(params?: {
    period?: 'upcoming' | 'past'
    planId?: string
    /** Restricts to one student of the account's scope (self or a dependent). */
    studentCustomerId?: string
    limit?: number
    cursor?: string
  }): Promise<PaginatedResponse<WorkoutListItem>> {
    const queryParams = new URLSearchParams()
    queryParams.append('period', params?.period ?? 'upcoming')
    if (params?.planId) queryParams.append('planId', params.planId)
    if (params?.studentCustomerId) queryParams.append('studentCustomerId', params.studentCustomerId)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.cursor) queryParams.append('cursor', params.cursor)

    const response = await this.get<PaginatedResponse<WorkoutListItem>>(
      `/me/workouts?${queryParams.toString()}`
    )
    return response.data
  }

  async getWorkoutDetail(id: string): Promise<WorkoutDetail> {
    const response = await this.get<{ data: WorkoutDetail }>(
      `/me/workouts/${encodeURIComponent(id)}`
    )
    return response.data.data
  }

  async getWorkoutCancelPreview(id: string): Promise<WorkoutCancelPreview> {
    const response = await this.get<{ data: WorkoutCancelPreview }>(
      `/me/workouts/${encodeURIComponent(id)}/cancel-preview`
    )
    return response.data.data
  }

  /**
   * Per-person preview — consequences differ per participation, so the batch
   * cancel confirmation reads one preview per selected person.
   */
  async getParticipantCancelPreview(id: string, participantId: string): Promise<WorkoutCancelPreview> {
    const response = await this.get<{ data: WorkoutCancelPreview }>(
      `/me/workouts/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/cancel-preview`
    )
    return response.data.data
  }

  async cancelWorkout(id: string, expectedKind?: WorkoutCancelKind): Promise<void> {
    await this.post(
      `/me/workouts/${encodeURIComponent(id)}/cancel`,
      expectedKind ? { expectedKind } : {}
    )
  }

  /**
   * Cancels the selected participations in one all-or-nothing call — each
   * with the `expectedKind` from THAT person's preview. A kind mismatch on any
   * of them cancels none.
   */
  async cancelWorkoutParticipants(
    id: string,
    participants: Array<{ participantId: string; expectedKind?: WorkoutCancelKind }>
  ): Promise<void> {
    await this.post(`/me/workouts/${encodeURIComponent(id)}/cancel`, { participants })
  }

  async confirmParticipation(id: string): Promise<void> {
    await this.post(`/me/workouts/${encodeURIComponent(id)}/confirm-participation`, {})
  }

  /**
   * Confirms the selected participations in one all-or-nothing call (room for
   * everyone or no one is confirmed).
   */
  async confirmParticipants(id: string, participantIds: string[]): Promise<void> {
    await this.post(`/me/workouts/${encodeURIComponent(id)}/confirm-participation`, { participantIds })
  }
}

export const workoutService = new WorkoutService()
